import { Tab, Tabs } from "@blueprintjs/core";
import { FilePondFile } from "filepond";
import { FormikProps } from "formik";
import { FGCustomInput, FGListen, FGSelectInput, FGTextInput, FieldGroup, useApiEffect } from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import { DogApi, DogChipApi, DrupalDogDto, EKennelType } from "../../../../api";
import { FGCountrySelect, FGSRSHDateMaskInput } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DogResultsList } from "../../../../pages/dog/dog/item/competition/results/DogResultsList";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";
import { DrupalSharepointFilesList } from "../../../common/DrupalSharepointFilesList";
import { EDrupalRoutes } from "../../../EDrupalRoutes";
import { DrupaldDogDysplasiaList } from "./DrupaldDogDysplasiaList";
import { DrupalDogCharacteristicList } from "./DrupalDogCharacteristicList";
import { DrupalDogTitleList } from "./DrupalDogTitleList";
import { isBrcs } from "../../../DrupalApp";

export interface IDrupalDogDetailPageProps {
  postSaveFunc?: any;
  isCreationPopUp?: boolean;
  readonlySex?: boolean;
  withoutOwner?: boolean;
  motherId?: number;
  isSportSection?: boolean;
}

export const DrupalDogDetailPage: React.FunctionComponent<IDrupalDogDetailPageProps> = ({
  postSaveFunc,
  withoutOwner = false,
  isCreationPopUp = false,
  readonlySex = true,
  motherId,
  isSportSection = null,
}) => {
  const { currentLanguage } = useGlobalData();
  const formikRef = React.useRef<FormikProps<DrupalDogDto>>();
  const { dId, tab = "title" } = useParams<{ tab: string; dId: string }>();
  const history = useHistory();
  const dogId = React.useMemo(() => (!isCreationPopUp ? +dId : 0), [dId, isCreationPopUp]);
  const [dogFiles, setDogFiles] = React.useState<{
    [fileName: string]: FilePondFile;
  }>({
    miscFiles: null,
    adnProfile: null,
    pedigree: null,
    breedingAdmission: null,
  });

  const { t } = useTl();
  const api = useApiService(DogApi);
  const chipApi = useApiService(DogChipApi);

  const { data, loading, saving, saveItem, deleting, deleteItem, refresh } = useCrudApi<DrupalDogDto>(
    React.useMemo(
      () => ({
        getApiFn: async (apiId = dogId) => {
          if ((apiId > 0 && !isCreationPopUp) || (apiId === 0 && isCreationPopUp)) {
            return api.getDrupalDog(apiId, isCreationPopUp);
          } else {
            return DrupalDogDto.fromJS({
              dogId: 0,
              isSportSection: isSportSection,
              hasAdnProfile: false,
              birthDate: new Date(),
            }).toJSON();
          }
        },
        saveApiFn: async (d: DrupalDogDto) => {
          const res = await api.saveDrupal(
            d.dogId,
            d.isSportSection,
            d.firstName,
            d.birthDate,
            d.deathDate,
            d.dogId,
            d.breedId,
            d.dogTypeId,
            d.colorId,
            d.sex,
            d.sharepointUri,
            d.dogName,
            d.tatoo,
            d.breedName,
            d.chipNumber,
            d.countryPrefixId,
            d.pedigree,
            null,
            null,
            d.hasAdnProfile,
            withoutOwner,
            dogFiles["miscFiles"]
              ? {
                  data: dogFiles["miscFiles"].file,
                  fileName: dogFiles["miscFiles"].filename,
                }
              : null,
            dogFiles["adnProfile"]
              ? {
                  data: dogFiles["adnProfile"].file,
                  fileName: dogFiles["adnProfile"].filename,
                }
              : null,
            dogFiles["pedigreeFile"]
              ? {
                  data: dogFiles["pedigreeFile"].file,
                  fileName: dogFiles["pedigreeFile"].filename,
                }
              : null,
            dogFiles["breedingAdmission"]
              ? {
                  data: dogFiles["breedingAdmission"].file,
                  fileName: dogFiles["breedingAdmission"].filename,
                }
              : null,
            dogFiles["healthProfile"]
              ? {
                  data: dogFiles["healthProfile"].file,
                  fileName: dogFiles["healthProfile"].filename,
                }
              : null
          );
          setDogFiles({
            miscFiles: null,
            adnProfile: null,
            pedigree: null,
            breedingAdmission: null,
          });
          formikRef.current.setFieldValue("miscFiles", null);
          formikRef.current.setFieldValue("adnProfile", null);
          formikRef.current.setFieldValue("pedigree", null);
          formikRef.current.setFieldValue("breedingAdmission", null);
          formikRef.current.setFieldValue("healthProfile", null);
          postSaveFunc && postSaveFunc(res);
          return res;
        },
        onSavedRoute: !isCreationPopUp
          ? (d) => {
              return `${EDrupalRoutes.dog}/${d.dogId}/files`;
            }
          : undefined,
        deleteApiFn: () => {},
      }),
      [api, dogFiles, dogId, isCreationPopUp, isSportSection, postSaveFunc, withoutOwner]
    ),
    [dogId]
  );

  React.useEffect(() => {
    if (data) {
      data.isSportSection = isSportSection;
    }
  }, [data, isSportSection]);

  const [files, fLoading] = useApiEffect(() => api.getDrupalDogFilesList(dogId), [data]);

  const downloadFile = React.useCallback(
    async (fileName) => {
      return api.downloadDogDoc(data?.sharepointUri, fileName);
    },
    [api, data?.sharepointUri]
  );

  const [breeds, bloading] = useReferential((a) => a.getBreeds(motherId), true, [motherId]);
  const [breedRequest, setBreedRequest] = React.useState(0);
  const [colors, coloading] = useReferential((a) => a.getColorsByBreed(breedRequest ? breedRequest : 0), true, [
    breedRequest,
  ]);
  const [sex, sloading] = useReferential((a) => a.getSex(currentLanguage.languageId), false, []);
  const [countryId, setCountryId] = React.useState<number>(0);
  const [countryPrefixes, cpLoading] = useReferential(
    (a) =>
      isCreationPopUp && !!countryId
        ? a.getPedigreePrefixesByCountry(countryId, null)
        : a.getAllForeignPedigreePrefixes(),
    true,
    [countryId]
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape(
      {
        firstName: Yup.string().nullable().required(t(ETLCodes.Required)),
        sex: Yup.string().nullable().required(t(ETLCodes.Required)),
        birthDate: Yup.string().nullable().required(t(ETLCodes.Required)),
        tatoo: Yup.string()
          .nullable()
          .when("chipNumber", (value, schema) =>
            value === undefined || value === null ? schema.required(t(ETLCodes.Required)) : schema
          ),
        countryId: Yup.number()
          .nullable()
          .when("dogId", { is: 0, then: Yup.number().nullable().required(t(ETLCodes.Required)) }),
        countryPrefixId: Yup.number()
          .nullable()
          .when("dogId", { is: 0, then: Yup.number().nullable().required(t(ETLCodes.Required)) }),
        pedigree: Yup.string()
          .nullable()
          .when("dogId", { is: 0, then: Yup.string().nullable().required(t(ETLCodes.Required)) }),
        chipNumber: Yup.string()
          .nullable()
          .when("tatoo", (value, schema) =>
            value === undefined || value === null ? schema.required(t(ETLCodes.Required)) : schema
          )
          .test("is-equal", t(ETLCodes.ChipNumberAlreadyInUse), async function (value) {
            if (!value) return true;
            const res = await chipApi.isChipUnique(value, !isCreationPopUp ? dogId : 0);
            return res.value;
          }),
        breedId: Yup.number().nullable().required(t(ETLCodes.Required)),
        colorId: Yup.number().nullable().required(t(ETLCodes.Required)),
      },
      [
        ["chipNumber", "tatoo"],
        ["pedigree", "countryPrefixId"],
      ]
    );
  }, [chipApi, dogId, isCreationPopUp, t]);

  const hasAdnProfile = React.useMemo(() => data?.hasAdnProfile, [data?.hasAdnProfile]);
  const isCreate = React.useMemo(() => dogId <= 0 || isCreationPopUp, [dogId, isCreationPopUp]);

  return (
    <>
      <DrupalFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={true}
        validationSchema={dogId < 1 || isCreationPopUp ? FormSchema : null}
        loading={loading}
        onDelete={deleteItem}
        showDeleteButton={dogId > 0}
        saving={saving}
        deleting={deleting}
        drupal={true}
        enableDirtyCheck={!isCreationPopUp}
        onCancel={() => (isCreationPopUp ? undefined : history.push(EDrupalRoutes.dog))}
        formikInnerRef={(f) => (formikRef.current = f)}
      >
        <FGListen
          field="breedId"
          onChanged={(value) => {
            setBreedRequest(value);
          }}
        />
        <FGListen
          field="countryId"
          onChanged={(value) => {
            setCountryId(value);
          }}
        />
        <FieldGroup columns={isCreationPopUp ? 1 : 2} fieldsetProps={{ title: t(ETLCodes.DogInformations) }}>
          <FieldGroup>
            <FGCustomInput name="deathDate" label={t(ETLCodes.CompleteName)} helperText="">
              {(ctx) => {
                if (dogId) {
                  return (
                    <div
                      style={{
                        paddingLeft: "10px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {ctx.formik.values?.kennelType === EKennelType.Prefix
                        ? `${ctx.formik.values?.kennelName ?? ""} ${ctx.formik.values?.firstName}`
                        : `${ctx.formik.values?.firstName} ${ctx.formik.values?.kennelName ?? ""}`}
                    </div>
                  );
                }
              }}
            </FGCustomInput>
            <FGTextInput name="firstName" label={t(ETLCodes.Name)} maxLength={30} readonly={!isCreate} />
            <FGSelectInput
              name="sex"
              label={t(ETLCodes.Sex)}
              items={sex}
              loading={sloading}
              requiredMark
              readonly={readonlySex}
            />
            <FGSRSHDateMaskInput name="birthDate" label={t(ETLCodes.BirthDate)} readonly={!isCreate} />
            {isCreationPopUp ? (
              <FGCountrySelect label={t(ETLCodes.Country)} includeBelgium={true} name="countryId" />
            ) : null}
            <>
              {isCreationPopUp ? (
                <FGSelectInput
                  name="countryPrefixId"
                  label={t(ETLCodes.PedigreePrefix)}
                  readonly={!isCreate}
                  items={countryPrefixes}
                  loading={cpLoading}
                />
              ) : null}

              <FGTextInput name="pedigree" label={t(ETLCodes.Pedigree)} readonly={!isCreate} />
            </>
            <FGTextInput
              requiredMark
              name="chipNumber"
              label={t(ETLCodes.ChipNumber)}
              maxLength={15}
              readonly={!isCreate}
            />
            <FGTextInput requiredMark name="tatoo" label={t(ETLCodes.Tatoo)} maxLength={80} readonly={!isCreate} />
            <FGSelectInput
              name="breedId"
              label={t(ETLCodes.Breed)}
              items={breeds}
              loading={bloading}
              readonly={!isCreate}
            />
            <FGSelectInput
              name="colorId"
              label={t(ETLCodes.Color)}
              items={colors}
              loading={coloading}
              readonly={!isCreate}
            />
            {!isCreationPopUp ? <FGSRSHDateMaskInput name="deathDate" label={t(ETLCodes.DeathDate)} /> : null}
          </FieldGroup>
          {!isCreationPopUp ? (
            <FieldGroup>
              {!hasAdnProfile && (
                <FGCustomInput name="adnProfile" label={t(ETLCodes.ADNProfile)}>
                  {(ctx) => (
                    <FilePond
                      labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                      onupdatefiles={(f) => {
                        ctx.formik.setFieldValue("adnProfile", f.length > 0 ? f[0] : null);
                        setDogFiles({
                          ...dogFiles,
                          adnProfile: f.length > 0 ? f[0] : null,
                        });
                      }}
                    />
                  )}
                </FGCustomInput>
              )}
              <FGCustomInput name="pedigreeFile" label={t(ETLCodes.Pedigree)}>
                {(ctx) => (
                  <FilePond
                    labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                    onupdatefiles={(f) => {
                      ctx.formik.setFieldValue("pedigreeFile", f.length > 0 ? f[0] : null);
                      setDogFiles({
                        ...dogFiles,
                        pedigreeFile: f.length > 0 ? f[0] : null,
                      });
                    }}
                  />
                )}
              </FGCustomInput>
              <FGCustomInput name="breedingAdmission" label={t(ETLCodes.BreedingAdmissionFiles)}>
                {(ctx) => (
                  <FilePond
                    labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                    onupdatefiles={(f) => {
                      ctx.formik.setFieldValue("breedingAdmission", f.length > 0 ? f[0] : null);
                      setDogFiles({
                        ...dogFiles,
                        breedingAdmission: f.length > 0 ? f[0] : null,
                      });
                    }}
                  />
                )}
              </FGCustomInput>
              <FGCustomInput name="miscFiles" label={t(ETLCodes.Miscellaneous)}>
                {(ctx) => (
                  <FilePond
                    labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                    onupdatefiles={(f) => {
                      ctx.formik.setFieldValue("miscFiles", f.length > 0 ? f[0] : null);
                      setDogFiles({
                        ...dogFiles,
                        miscFiles: f.length > 0 ? f[0] : null,
                      });
                    }}
                  />
                )}
              </FGCustomInput>
            </FieldGroup>
          ) : null}
        </FieldGroup>
        {isCreationPopUp ? (
          <>
            <FGCustomInput name="pedigreeFile" label={t(ETLCodes.Pedigree)}>
              {(ctx) => (
                <FilePond
                  labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                  onupdatefiles={(f) => {
                    ctx.formik.setFieldValue("pedigreeFile", f.length > 0 ? f[0] : null);
                    setDogFiles({
                      ...dogFiles,
                      pedigreeFile: f.length > 0 ? f[0] : null,
                    });
                  }}
                />
              )}
            </FGCustomInput>
            <FGCustomInput name="adnProfile" label={t(ETLCodes.ADNProfile)}>
              {(ctx) => (
                <FilePond
                  labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                  onupdatefiles={(f) => {
                    ctx.formik.setFieldValue("adnProfile", f.length > 0 ? f[0] : null);
                    setDogFiles({
                      ...dogFiles,
                      adnProfile: f.length > 0 ? f[0] : null,
                    });
                  }}
                />
              )}
            </FGCustomInput>

            <FGCustomInput name="healthProfile" label={t(ETLCodes.HealthProfile)}>
              {(ctx) => (
                <FilePond
                  labelIdle={t(ETLCodes.FilePondDragOrSelect)}
                  onupdatefiles={(f) => {
                    ctx.formik.setFieldValue("healthProfile", f.length > 0 ? f[0] : null);
                    setDogFiles({
                      ...dogFiles,
                      healthProfile: f.length > 0 ? f[0] : null,
                    });
                  }}
                />
              )}
            </FGCustomInput>
          </>
        ) : null}
      </DrupalFormGenerator>
      {tab && !isCreationPopUp && (
        <Tabs
          id="tabsDrupalDog"
          onChange={(newTabId) => {
            history.push(`${EDrupalRoutes.dog}/${dId}/${newTabId}`);
          }}
          selectedTabId={tab}
          large={true}
        >
          {dogId > 0 && (
            <Tab
              id="files"
              title={t(ETLCodes.Files)}
              panel={<DrupalSharepointFilesList loading={fLoading} files={files} downloadFunc={downloadFile} />}
            />
          )}
          {!isCreationPopUp && (
            <Tab
              id="characteristic"
              title={t(ETLCodes.Characteristic)}
              panel={<DrupalDogCharacteristicList dogId={dogId} refresh={refresh} />}
            />
          )}
          {!isCreationPopUp && (
            <Tab id="title" title={t(ETLCodes.Title)} panel={<DrupalDogTitleList dogId={dogId} refresh={refresh} />} />
          )}

          {!isCreationPopUp && (
            <Tab
              id="competitionResults"
              title={t(ETLCodes.CompetitionResults)}
              panel={<DogResultsList dogId={dogId} editMode={false} />}
            />
          )}
          {!isCreationPopUp && (
            <Tab id="dysplasia" title={t(ETLCodes.Dysplasia)} panel={<DrupaldDogDysplasiaList dogId={dogId} />} />
          )}
        </Tabs>
      )}
    </>
  );
};
